import { raiseTitle, type RaiseDraft, type RaiseKind } from '../lib/acceptance'
import { api } from '../lib/api'
import { exceptionsApi } from '../lib/exceptions'
import { exitApi } from '../lib/exit'
import { mfaApi } from '../lib/mfa'
import type { DialogFooterProps } from './kit'

// What a dialog's footer holds, and what its main action sends, worked out
// from what its form holds.
//
// # WHY THESE ARE NOT IN THE COMPONENTS
//
// Which button is the main one, when it is off and why, whether it is drawn as
// destructive, and what request a press sends are decisions. Written inside a
// component they can be checked only by pressing the button in a browser, and
// a slip (Decline sent as Sign, a reason left out of the request) passes every
// test that only reads the page. Here each is a plain function of the form's
// state: the component passes its state in and spreads the answer on its
// Dialog, and the tests call the same function, press its main action and
// read the request.
//
// `send` is the dialog's own way of sending (lib/save.ts): the function hands
// it the request and has no say in what becomes of the answer.

type Send<T> = (work: Promise<T>) => void
type Answer<F extends (...args: never[]) => Promise<unknown>> = Awaited<ReturnType<F>>

// ------------------------------------------------------------ the exit desk

export function signFooter(
  s: { vendorId: string; stageKey: string; decision: 'SIGN' | 'DECLINE'; note: string; busy: boolean },
  send: Send<Answer<typeof exitApi.decide>>,
): DialogFooterProps {
  const decline = s.decision === 'DECLINE'
  return {
    busy: s.busy,
    primary: {
      label: s.busy ? 'Recording' : decline ? 'Decline' : 'Sign',
      tone: decline ? 'destructive' : 'primary',
      off: decline && s.note.trim() === '' && 'Say what would have to change',
      onClick: () => send(exitApi.decide(s.vendorId, { stageKey: s.stageKey, decision: s.decision, note: s.note.trim() })),
      actionId: 'tprm.exit-workspace,signoffs,vendor,vendor-record.a01',
    },
  }
}

// Starting an exit moves the vendor to offboarding, and dismissing a proposal
// stops the same fact proposing it again: neither is drawn as the easy button.
export function proposalFooter(
  s: { exitId: string; act: 'confirm' | 'dismiss'; reason: string; busy: boolean },
  send: Send<Answer<typeof exitApi.confirm>>,
): DialogFooterProps {
  const dismiss = s.act === 'dismiss'
  return {
    busy: s.busy,
    primary: {
      label: s.busy ? 'Recording' : dismiss ? 'Dismiss' : 'Start offboarding',
      tone: 'destructive',
      off: dismiss && s.reason.trim() === '' && 'Say why it is not pursued',
      onClick: () => send(dismiss ? exitApi.dismiss(s.exitId, s.reason.trim()) : exitApi.confirm(s.exitId, s.reason.trim())),
      actionId: 'tprm.exit-workspace,vendor,vendor-record.a04',
      actionAlso: 'tprm.exit-workspace,vendor,vendor-record.a05',
    },
  }
}

export function placeHoldFooter(
  s: { vendorId: string; reason: string; reference: string; busy: boolean },
  send: Send<Answer<typeof exitApi.placeLegalHold>>,
): DialogFooterProps {
  return {
    busy: s.busy,
    primary: {
      label: s.busy ? 'Placing' : 'Place the hold',
      off: s.reason.trim() === '' && 'Say why the data has to be kept',
      onClick: () => send(exitApi.placeLegalHold(s.vendorId, { reason: s.reason, reference: s.reference })),
      actionId: 'tprm.exit-workspace.a06',
    },
  }
}

// Releasing a hold lets the data it protected be destroyed.
export function releaseHoldFooter(
  s: { vendorId: string; holdId: string; reason: string; busy: boolean; mayRelease: boolean },
  send: Send<Answer<typeof exitApi.releaseLegalHold>>,
): DialogFooterProps {
  if (!s.mayRelease) return {}
  return {
    busy: s.busy,
    primary: {
      label: s.busy ? 'Releasing' : 'Release the hold',
      tone: 'destructive',
      off: s.reason.trim() === '' && 'Say why it is released',
      onClick: () => send(exitApi.releaseLegalHold(s.vendorId, s.holdId, s.reason)),
      actionId: 'tprm.exit-workspace.a07',
    },
  }
}

// ------------------------------------------------------------------- tiers

// The three tier dialogs check what was typed when the button is pressed, so
// `mark` records that it was pressed and the count follows from then on.
export function tierAskFooter(
  s: { vendorId: string; tier: string; reason: string; ends: string; tried: boolean; busy: boolean },
  send: Send<Answer<typeof api.requestTierOverride>>,
  mark: () => void,
): DialogFooterProps {
  const wrong = (s.tier === '' ? 1 : 0) + (s.reason.trim() === '' ? 1 : 0)
  return {
    busy: s.busy,
    problems: s.tried ? wrong : 0,
    primary: {
      label: s.busy ? 'Sending' : 'Send for approval',
      onClick: () => {
        mark()
        if (wrong > 0) return
        send(api.requestTierOverride(s.vendorId, { tier: Number(s.tier), reason: s.reason.trim(), expiresOn: s.ends || undefined }))
      },
      actionId: 'tprm.vendor,vendor-record.a32',
    },
  }
}

export function tierRejectFooter(
  s: { vendorId: string; requestId: string; note: string; tried: boolean; busy: boolean },
  send: Send<Answer<typeof api.decideTierOverride>>,
  mark: () => void,
): DialogFooterProps {
  const wrong = s.note.trim() === '' ? 1 : 0
  return {
    busy: s.busy,
    problems: s.tried ? wrong : 0,
    primary: {
      label: s.busy ? 'Saving' : 'Reject',
      tone: 'destructive',
      onClick: () => {
        mark()
        if (wrong > 0) return
        send(api.decideTierOverride(s.vendorId, s.requestId, 'reject', s.note.trim()))
      },
      actionId: 'tprm.vendor,vendor-record.a34',
    },
  }
}

export function tierEndFooter(
  s: { vendorId: string; overrideId: string; note: string; busy: boolean },
  send: Send<Answer<typeof api.endTierOverride>>,
): DialogFooterProps {
  return {
    busy: s.busy,
    primary: {
      label: s.busy ? 'Ending' : 'End manual tier',
      onClick: () => send(api.endTierOverride(s.vendorId, s.overrideId, s.note.trim() || undefined)),
      actionId: 'tprm.vendor,vendor-record.a36',
    },
  }
}

// ------------------------------------------------------------------ people

export function resetFooter(s: { subject: string | null; busy: boolean }, send: Send<Answer<typeof mfaApi.resetFor>>): DialogFooterProps {
  const subject = s.subject
  return {
    busy: s.busy,
    primary: {
      label: s.busy ? 'Removing' : 'Remove the second factor',
      tone: 'destructive',
      onClick: () => {
        if (subject !== null) send(mfaApi.resetFor(subject))
      },
    },
  }
}

// ------------------------------------------------- a risk acceptance, open

// The record's own two decisions each open a reason box. While one is open
// the footer is that box's two buttons and nothing else: Cancel puts the box
// away and the record's actions come back.
export function acceptanceFooter(
  s: { id: string; rejecting: boolean; remediating: boolean; rejectIssue: boolean; remediateIssue: boolean; busy: boolean },
  back: { reject: () => void; remediate: () => void },
): DialogFooterProps {
  if (s.rejecting) {
    return {
      busy: s.busy,
      problems: s.rejectIssue ? 1 : 0,
      cancel: { onClick: back.reject },
      primary: { label: 'Confirm rejection', tone: 'destructive', form: 'reject-' + s.id, actionId: 'tprm.exceptions.a08' },
    }
  }
  if (s.remediating) {
    return {
      busy: s.busy,
      problems: s.remediateIssue ? 1 : 0,
      cancel: { onClick: back.remediate },
      primary: { label: 'Send it back', tone: 'plain', form: 'remediate-' + s.id, actionId: 'tprm.exceptions.a09' },
    }
  }
  return { busy: s.busy }
}

// What submitting a reason box does: nothing but ask for the reason when it is
// empty, otherwise the request.
export function decideAcceptance(
  kind: 'reject' | 'remediate',
  id: string,
  reason: string,
): { missing: true } | { missing: false; work: ReturnType<typeof api.rejectException> } {
  const said = reason.trim()
  if (said === '') return { missing: true }
  return { missing: false, work: kind === 'reject' ? api.rejectException(id, said) : api.remediateException(id, said) }
}

// ------------------------------------------ a stage of a chain of signatures

export function stageSignFooter(
  s: { index: number; note: string; why: string; busy: boolean; actionId: string },
  sign: (index: number, note: string) => void,
  clear: () => void,
): DialogFooterProps {
  return {
    busy: s.busy,
    primary: {
      label: s.busy ? 'Recording' : 'Sign this stage',
      off: s.why !== '' && 'Not yours to sign',
      actionId: s.actionId,
      onClick: () => {
        sign(s.index, s.note)
        clear()
      },
    },
  }
}

// ------------------------------------------------------- a finding, open

export type FindingMove = { to: string; requiresNote: boolean }

// The moves a finding offers, as its dialog's footer. Resolving is the main
// one. A move that needs a reason opens the reason box first; with the box
// open the footer is that move and Cancel, and Cancel puts the box away.
export function findingFooter<M extends FindingMove>(
  s: { moves: M[]; pending: M | null; missing: boolean; refusal: string; busy: boolean; label: (to: string) => string },
  act: { press: (m: M) => void; confirm: (m: M) => void; back: () => void },
): Omit<DialogFooterProps, 'more'> & { others: M[] } {
  const pending = s.pending
  if (pending) {
    return {
      busy: s.busy,
      problems: s.missing ? 1 : 0,
      cancel: { onClick: act.back },
      others: [],
      primary: {
        label: s.busy ? 'Recording' : s.label(pending.to),
        onClick: () => act.confirm(pending),
        actionId: 'tprm.assessment,findings,risk,vendor,vendor-record.a04',
        actionAlso: 'tprm.assessment,findings,risk,vendor,vendor-record.a05',
      },
    }
  }
  const main = s.moves.find((m) => m.to === 'RESOLVED')
  return {
    busy: s.busy,
    others: s.moves.filter((m) => m !== main),
    primary: main && {
      label: s.label(main.to),
      off: s.refusal,
      onClick: () => act.press(main),
      actionId: 'tprm.assessment,findings,risk,vendor,vendor-record.a05',
    },
  }
}

// ------------------------------------------------- what the long forms send

export function findingBody(d: {
  title: string
  detail: string
  citation: string
  severity: string
  dueOn: string
  ownerUserId: string
  riskType: string
}): Parameters<typeof api.raiseFinding>[1] {
  return {
    title: d.title.trim(),
    detail: d.detail.trim() || undefined,
    citation: d.citation.trim() || undefined,
    severity: d.severity,
    dueOn: d.dueOn || undefined,
    ownerUserId: d.ownerUserId.trim() || undefined,
    riskType: d.riskType || undefined,
  }
}

export function exceptionBody(d: {
  vendorId: string
  findingId: string
  gateKey: string
  title: string
  justification: string
  compensatingControl: string
  ownerUserId: string
  expiresOn: string
  parameterCode: string
  severity: string
  correctiveAction: string
  targetClosureOn: string
  vendorResponse: string
  vendorResponseOn: string
}): Parameters<typeof exceptionsApi.create>[0] {
  return {
    vendorId: d.vendorId,
    findingId: d.findingId || undefined,
    gateKey: d.gateKey.trim() || undefined,
    title: d.title.trim(),
    justification: d.justification.trim(),
    compensatingControl: d.compensatingControl.trim(),
    ownerUserId: d.ownerUserId.trim(),
    expiresOn: d.expiresOn,
    parameterCode: d.parameterCode,
    severity: d.severity,
    correctiveAction: d.correctiveAction.trim() || undefined,
    targetClosureOn: d.targetClosureOn || undefined,
    vendorResponse: d.vendorResponse.trim() || undefined,
    vendorResponseOn: d.vendorResponseOn || undefined,
  }
}

// A written risk acceptance or a contract waiver, raised from the vendor.
export function raiseBody(kind: RaiseKind, vendor: { id: string; name: string; band: string }, d: RaiseDraft): Parameters<typeof api.createException>[0] {
  return {
    vendorId: vendor.id,
    kind,
    title: raiseTitle(kind, vendor.name, vendor.band),
    justification: d.justification.trim(),
    compensatingControl: d.compensatingControl.trim(),
    ownerUserId: d.ownerUserId.trim(),
    expiresOn: d.expiresOn,
  }
}

export function overrideBody(d: { band: string; reason: string; approvedBy: string; expiresOn: string }): Parameters<typeof api.overrideBand>[1] {
  return { band: d.band, reason: d.reason.trim(), approvedBy: d.approvedBy.trim(), expiresOn: d.expiresOn }
}
